import { getContactOriginLabel, SYSTEM_CONTACT_ORIGINS } from "../../@types/sharedSchema";

// MANAGED lead-source values, shared by BOTH lead-source pickers in the app:
// the new/edit-contact modal (`EditContactDialog`) and the CRM table's in-cell
// editor (`CrmCellControls.CellOrigin`). One definition on purpose — two lists
// of lead sources is two vocabularies, and a realtor who picks "יד2" in one
// place and something spelled differently in the other has made the provenance
// uncountable, which is the only thing this field is for.
//
// `contactOrigin` remains a free-text string on the contact doc (historical
// values + system-stamped values live there), and both pickers still offer a
// "type your own" row — but typing is the exception rather than the input
// method, and the presets are what keeps the common answers to one spelling.
// Yad2 and Madlan are deliberately SEPARATE values — never merge them back into
// one option.
//
// The CANONICAL system-written values (webhook, mcp, 'AI call transcription', …)
// and their Hebrew display labels are centralized in sharedSchema.ts
// (SYSTEM_CONTACT_ORIGINS / getContactOriginLabel) — shared with the server,
// which stamps them. Use getContactOriginLabel() wherever an origin is shown.
/**
 * Named because the calls page stamps it on a contact the realtor creates from an inbound
 * call row — a second copy of the literal there would be a second vocabulary the moment
 * either is reworded, which is the one thing this file exists to prevent.
 *
 * NOT `SYSTEM_CONTACT_ORIGINS.aiCallTranscription`: that value means "callsProcessing created
 * this contact by itself", a different event from a realtor pressing יצירת לקוח.
 */
export const INCOMING_CALL_CONTACT_ORIGIN = "שיחה נכנסת";

export const CONTACT_ORIGIN_PRESETS: string[] = [
  "וואטסאפ",
  "פייסבוק",
  "אינסטגרם",
  "המלצה",
  "שלט / באנר",
  "יד2",
  "מדלן",
  "אתר",
  INCOMING_CALL_CONTACT_ORIGIN,
  "בוט Webot"
];

// Stamped on a contact created manually from the dashboard when the realtor
// didn't pick a source (keeps the required contactOrigin field populated).
export const MANUAL_CONTACT_ORIGIN = SYSTEM_CONTACT_ORIGINS.manual;

/**
 * The "let me type my own" row's sentinel. It is a MENU value, never a stored
 * one — picking it opens a text input; what the realtor types is what lands on
 * the document.
 *
 * Lives here rather than in either picker because BOTH render the row now (the
 * contact modal and the CRM table's in-cell editor), and two copies of a
 * sentinel is two chances for one of them to store `__custom__` as a lead
 * source.
 */
export const CUSTOM_CONTACT_ORIGIN_VALUE = "__custom__";

/**
 * The rows a lead-source picker offers, given what the contact currently holds.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A FUNCTION AND NOT JUST `CONTACT_ORIGIN_PRESETS`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `contactOrigin` is free text on the document and most of its real values were
 * never typed by a realtor: eight canonical values are stamped by SYSTEM writers
 * (`SYSTEM_CONTACT_ORIGINS` — `AI call transcription`, `webhook`, `mcp`, …), and
 * years of imports left arbitrary strings behind. A picker offering only the ten
 * presets would show such a contact NOTHING selected, and the first save would
 * replace a value its own writer recognises with one it does not.
 *
 * So the contact's own value is folded into the list. Three rules, in order:
 *
 *  1. **`הוזן ידנית` is never offered.** It is the "nobody picked a source"
 *     sentinel written on save, not a source. Offering it invites realtors to
 *     select it as though it meant something.
 *  2. **A LABEL collision is resolved in favour of the stored value.** The
 *     presets contain the literal `"בוט Webot"`, and `getContactOriginLabel`
 *     renders the system value `'Homely with webot AI'` as that same string —
 *     so a bot-stamped contact used to produce TWO menu rows both reading
 *     "בוט Webot", one selected and one not. Substituting in place keeps the
 *     preset's position, shows one row, and keeps the value that is actually on
 *     the document (so re-picking it is a no-op rather than a rewrite).
 *  3. Anything else outside the presets is appended, so it stays visible and
 *     re-selectable.
 *
 * Yad2 and Madlan stay SEPARATE presets — never merge them; provenance has to
 * remain countable.
 */
export function buildContactOriginOptions(current?: string | null): string[] {
  const stored = (current ?? "").trim();
  if (!stored || stored === MANUAL_CONTACT_ORIGIN || CONTACT_ORIGIN_PRESETS.includes(stored)) {
    return [...CONTACT_ORIGIN_PRESETS];
  }

  const storedLabel = getContactOriginLabel(stored);
  let substituted = false;
  const options = CONTACT_ORIGIN_PRESETS.map((preset) => {
    if (substituted || getContactOriginLabel(preset) !== storedLabel) return preset;
    substituted = true;
    return stored;
  });

  return substituted ? options : [...options, stored];
}
